import { useEffect } from 'react';

/**
 * How long to keep the height frozen after undocking. Matches the 300ms
 * transition the search box eases home over, so the freeze outlives the
 * keyboard's descent and the layout restores once, at the end, rather than
 * walking back underneath a box that is still moving.
 */
const RELEASE_DELAY_MS = 300;

/**
 * Freeze depth, not a boolean: a release scheduled by one undock can land after
 * the next focus has already re-frozen, and counting makes that self-correcting
 * — the late release decrements to 1, not to 0, and the height stays held.
 */
let freezeDepth = 0;

/** Set by the mounted useViewportHeight, so a release can resync the height. */
let resync: (() => void) | null = null;

/**
 * Holds --vh at its current value. Every freeze must be matched by a release.
 */
export function freezeViewportHeight() {
  freezeDepth += 1;
}

/**
 * Drops one freeze, and resyncs --vh to the real viewport once the last one
 * lifts — otherwise the height would stay stuck at whatever the keyboard left.
 */
export function releaseViewportHeight() {
  freezeDepth = Math.max(0, freezeDepth - 1);
  if (freezeDepth === 0) resync?.();
}

/**
 * Owns the --vh custom property: one hundredth of the viewport height, which
 * index.css uses to size html and body.
 *
 * Needed because 100vh does not account for the keyboard on iOS, leaving a gap
 * below the content that the user can overscroll into.
 *
 * Mount once, at the app root.
 */
function useViewportHeight() {
  useEffect(() => {
    const updateVH = () => {
      // While frozen, --vh keeps the value it had when the freeze started. The
      // keyboard fires a burst of resizes as it opens and closes, and letting
      // each one through would resize the document — and so move every element
      // on the page — repeatedly, mid-animation.
      if (freezeDepth > 0) return;
      try {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      } catch (error) {
        // Silently fail if CSS custom property setting fails
        console.warn('Failed to set --vh CSS custom property:', error);
      }
    };

    const viewport = window.visualViewport;

    resync = updateVH;
    window.addEventListener('resize', updateVH);
    viewport?.addEventListener('resize', updateVH);
    updateVH(); // initial run

    return () => {
      resync = null;
      window.removeEventListener('resize', updateVH);
      viewport?.removeEventListener('resize', updateVH);
    };
  }, []);
}

/**
 * Freezes the document height for as long as `active`, plus the length of the
 * return animation.
 *
 * Used while the search box is docked to the keyboard. On Android's adjustResize
 * the WebView is resized repeatedly through the keyboard's open and close
 * animations; each resize rewrites --vh, which resizes html and body, which
 * moves the whole page. During the dock that fights the hold; during the return
 * it walks the layout back in steps while the box eases smoothly to rest, so
 * the two compose into a stutter. Holding the height still removes the source.
 *
 * The release is deliberately late — the box is undocked the moment focus is
 * lost, but the keyboard is still on its way down.
 */
export function useViewportHeightFreeze(
  active: boolean,
  releaseDelay: number = RELEASE_DELAY_MS,
) {
  useEffect(() => {
    if (!active) return undefined;
    freezeViewportHeight();
    return () => {
      if (releaseDelay === 0) releaseViewportHeight();
      else window.setTimeout(releaseViewportHeight, releaseDelay);
    };
  }, [active, releaseDelay]);
}

export default useViewportHeight;
